/**
 * China Configuration Registry
 *
 * Single source of truth for all China-specific feature policy.
 * - API endpoint routing → apiConfig.js
 * - Feature policy (models, UI behaviour) → this file
 *
 * Rules that apply to CN users:
 *  1. Only CAC/MIIT-licensed models may be used for API calls.
 *  2. The AI module selector (ModelPanel) is hidden unless Developer Mode is enabled.
 *  3. Developer Mode in CN requires a passphrase to unlock.
 *  4. Generic model names are shown outside the module selector for ALL users globally.
 */

import { isCnRegion } from './apiConfig';
export { isCnRegion };

// ---------------------------------------------------------------------------
// CN model policy
// ---------------------------------------------------------------------------

/** Model IDs with valid CAC / MIIT licensing for mainland China. */
export const CN_ALLOWED_MODELS = ['deepseek-r1', 'deepseek-v3'];

/** Fallback model used when a CN user's stored preference is not CN-allowed. */
export const CN_DEFAULT_MODEL = 'deepseek-v3';

/**
 * Filter a model list to only those allowed in CN region.
 * @param {Array} allModels - Full AI_MODELS array
 * @returns {Array}
 */
export function getCNAvailableModels(allModels) {
  return allModels.filter(m => CN_ALLOWED_MODELS.includes(m.id));
}

/**
 * Return the model ID that should actually be sent to the API.
 * For CN users, silently falls back to CN_DEFAULT_MODEL if the stored
 * preference is not CN-licensed — unless Developer Mode is active (which
 * grants access to all models even in CN region).
 * @param {string} modelId - Currently selected model ID
 * @param {boolean} [developerMode=false] - Whether developer mode is unlocked
 * @returns {string}
 */
export function getEffectiveModel(modelId, developerMode = false) {
  if (isCnRegion() && !developerMode && !CN_ALLOWED_MODELS.includes(modelId)) {
    return CN_DEFAULT_MODEL;
  }
  return modelId;
}

// ---------------------------------------------------------------------------
// Generic model display names (applies to ALL users, globally)
//
// These names are used OUTSIDE the developer module selector — in upgrade
// prompts, feature descriptions, marketing copy, etc.
// The module selector itself (developer mode only) shows real model names.
// ---------------------------------------------------------------------------

export const MODEL_GENERIC_NAMES = {
  // OpenAI
  'gpt-5.1':       'Powerful Thinking Model',
  'gpt-5':         'Powerful Thinking Model',
  'gpt-5-mini':    'Fast Thinking Model',
  'gpt-4.1':       'Powerful Thinking Model',
  'gpt-4.1-mini':  'Fast Thinking Model',
  'gpt-4o':        'Powerful Thinking Model',
  // Anthropic
  'claude-4.5-opus':    'Powerful Thinking Model',
  'claude-4.5-sonnet':  'Balanced Thinking Model',
  'claude-4.5-haiku':   'Fast Thinking Model',
  // Google
  'gemini-3.1-pro-preview': 'Powerful Thinking Model',
  'gemini-3-flash-preview':  'Fast Thinking Model',
  'gemini-3-flash-direct':   'Fast Thinking Model',
  'gemini-3.5-flash-lite':   'Fast Thinking Model',
  'gemini-3.1-flash-lite':   'Efficient Thinking Model',
  'gemini-2.5-flash':        'Fast Thinking Model',
  // DeepSeek
  'deepseek-r1': 'Deep Reasoning Model',
  'deepseek-v3': 'Efficient Thinking Model',
};

/**
 * Get the generic display name for a model.
 * Use this everywhere OUTSIDE the developer module selector.
 * @param {string} modelId
 * @returns {string}
 */
export function getModelGenericName(modelId) {
  return MODEL_GENERIC_NAMES[modelId] || 'AI Thinking Model';
}

// ---------------------------------------------------------------------------
// Developer mode passphrase gate (all users globally)
// ---------------------------------------------------------------------------

/** Passphrase required by ALL users to unlock the Developer Settings toggle. */
export const CN_DEV_PASS = 'DevPass1!';

/** localStorage key that records whether the dev passphrase has been entered on this device. */
export const DEV_PASS_STORAGE_KEY = 'reedle-dev-unlocked';

/** @deprecated Use DEV_PASS_STORAGE_KEY */
export const CN_DEV_PASS_STORAGE_KEY = DEV_PASS_STORAGE_KEY;

// ---------------------------------------------------------------------------
// China 备案 (MIIT algorithm registration) registry
// ---------------------------------------------------------------------------

/**
 * AI algorithm registrations filed with China's Cyberspace Administration /
 * MIIT under the Administrative Provisions on Deep Synthesis.
 * Displayed in the About modal for all users (regulatory transparency).
 * To add a new entry: append to this array — no other files need changing.
 */
export const CN_BEIAN_REGISTRATIONS = [
  {
    name: '千问',
    algorithm: '达摩院交互式多能型合成算法',
    provider: '阿里巴巴达摩院（杭州）科技有限公司',
    license: '网信算备330110507206401230035号',
  },
  {
    name: '万相',
    algorithm: '达摩院图像合成算法',
    provider: '阿里巴巴达摩院（杭州）科技有限公司',
    license: '网信算备330110507206401230027号',
  },
];
